import { useTheme } from '@emotion/react';
import { Flex } from '@theme-ui/components';
import { FormattedMessage } from 'react-intl';

import {
  BoldText,
  CardLabel,
  CheckIconWrapper,
  GreyedOutText,
  Title,
} from '@app/components/Card/Card';
import FormattedMoney from '@app/components/FormattedMoney';
import { rem } from '@app/styles/theme/layout';

import { getVariantLabel } from '@app/helpers/shop';
import { CheckmarkIcon } from './CheckmarkIcon';
import type { LicenseCardProps } from './LicenseCard.constants';
import { LicenseCardVariant } from './LicenseCard.constants';
import messages from './LicenseCard.messages';
import { LicenseCardWrapper } from './LicenseCard.styles';

export const LicenseCard = ({
  variant = LicenseCardVariant.Default,
  productVariant,
  isSelected = false,
  sx,
  ...props
}: LicenseCardProps) => {
  const theme = useTheme();
  const variantTitle = getVariantLabel(productVariant);

  const regularPrice =
    productVariant?.product?.priceRange?.minimumPrice?.regularPrice;

  const finalPrice =
    productVariant?.product?.priceRange?.minimumPrice?.finalPrice;

  return (
    <LicenseCardWrapper
      data-testid={`${variant}-license-card`}
      variant={variant}
      highlight={isSelected}
      sx={{ width: '100%', ...sx }}
      {...props}
    >
      <Flex
        sx={{
          width: '90%',
          padding: rem(16),
          justifyContent: 'space-between',
          flex: 1,
        }}
      >
        {isSelected && (
          <CheckIconWrapper data-testid="selected-check-icon">
            <CheckmarkIcon type={variant} />
          </CheckIconWrapper>
        )}

        <Flex
          sx={{
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {variantTitle && (
            <Title
              data-testid={`license-${productVariant?.product?.sku}-title`}
            >
              {variantTitle}
            </Title>
          )}

          <Flex
            data-testid={`variant-${productVariant?.product?.sku}-price`}
            sx={{
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {regularPrice?.value !== finalPrice?.value && (
              <GreyedOutText data-testid="variant-regular-price">
                <FormattedMoney {...regularPrice} />
              </GreyedOutText>
            )}
            {finalPrice && (
              <BoldText data-testid="variant-final-price">
                <FormattedMoney {...finalPrice} />
              </BoldText>
            )}
          </Flex>
        </Flex>
      </Flex>

      {variant === LicenseCardVariant.Recommendation && (
        <CardLabel
          color={theme.colors.acidGreen}
          textColor={theme.colors.white}
        >
          <FormattedMessage {...messages.bestOption} />
        </CardLabel>
      )}
    </LicenseCardWrapper>
  );
};
